import useSWR from 'swr';
import api from 'utils/api';
import type { Artist, Pagination, PaginatedResponse, UserInfo } from 'types';

// ===== Admin Collab Club matches listing types (GET /admin/cc-matches) =====
// Co-located here because they describe this endpoint's request/response contract
// and are only consumed by this hook and the Collab Club admin page.

export interface AdminCcMatch {
  id: number;
  method: 'auto' | 'team';
  // Effective (off-platform) date of the match; falls back to createdAt for display.
  matchDate: string | null;
  createdAt: string;
  updatedAt: string;
  artist1: Artist;
  artist2: Artist;
  arUser1: UserInfo | null;
  arUser2: UserInfo | null;
  // Admin who logged the match (team matches only; null for auto-matched pairs).
  creator: UserInfo | null;
}

export type CcMatchOrderBy =
  | 'createdAt'
  | 'matchDate'
  | 'method'
  | 'artist1'
  | 'artist2'
  | 'creator';

export interface AdminCcMatchesParams {
  scope?: 'mine' | 'all';
  search?: string;
  orderBy?: CcMatchOrderBy;
  orderDirection?: 'ASC' | 'DESC';
  page?: number;
  limit?: number;
}

interface UseAdminCcMatchesResult {
  matches: AdminCcMatch[];
  pagination: Pagination | null;
  totalItems: number;
  isLoading: boolean;
  error: any;
  refetch: () => void;
}

/**
 * Lists Collab Club matches for the admin page (GET /admin/cc-matches).
 * Server-side sort / pagination — see backend docs/collab-club-api.md.
 */
export function useAdminCcMatches(params: AdminCcMatchesParams): UseAdminCcMatchesResult {
  // Drop empty values so the request only carries meaningful query params.
  const queryParams: Record<string, string | number> = {};
  if (params.scope) queryParams.scope = params.scope;
  if (params.search?.trim()) queryParams.search = params.search.trim();
  if (params.orderBy) queryParams.orderBy = params.orderBy;
  if (params.orderDirection) queryParams.orderDirection = params.orderDirection;
  if (params.page) queryParams.page = params.page;
  if (params.limit) queryParams.limit = params.limit;

  const {
    data,
    error,
    mutate: refetch,
  } = useSWR<PaginatedResponse<AdminCcMatch>>(
    ['admin-cc-matches', JSON.stringify(queryParams)],
    () => api.collabClub.getAdminMatches({ params: queryParams }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  return {
    matches: data?.data || [],
    pagination: data?.pagination || null,
    totalItems: data?.pagination?.totalItems ?? 0,
    isLoading: !data && !error,
    error,
    refetch,
  };
}
